import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ReservationSection = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    service: '',
    dateDepart: '',
    heureDepart: '',
    lieuDepart: '',
    destination: '',
    nombrePassagers: '1',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const services = [
    'Transfert Aéroport',
    'Circuit Touristique',
    'Service de transport pour équipages aérien',
    'Excursion Journée',
    'Location avec Chauffeur',
    'Service Canada'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3tpr0djnnh8vq2jupig', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          service: '',
          dateDepart: '',
          heureDepart: '',
          lieuDepart: '',
          destination: '',
          nombrePassagers: '1',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reservation" className="py-12 sm:py-16 md:py-20 bg-sand-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal-800 mb-4 sm:mb-6">
            {t('common:reservationTitle')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-charcoal-600 max-w-3xl mx-auto leading-relaxed px-4">
            {t('common:reservationDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-start">
          {/* Formulaire */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
            <form id="reservation-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="nom" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:fullName')} {t('common:required')}
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderName')}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:email')} {t('common:required')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderEmail')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="telephone" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:phone')} {t('common:required')}
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderPhone')}
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:desiredService')} {t('common:required')}
                  </label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm pr-8 appearance-none"
                    >
                      <option value="">{t('common:selectService')}</option>
                      {services.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="dateDepart" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:departureDate')} {t('common:required')}
                  </label>
                  <input
                    type="date"
                    id="dateDepart"
                    name="dateDepart"
                    value={formData.dateDepart}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="heureDepart" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:departureTime')}
                  </label>
                  <input
                    type="time"
                    id="heureDepart"
                    name="heureDepart"
                    value={formData.heureDepart}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="nombrePassagers" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:passengers')}
                  </label>
                  <div className="relative">
                    <select
                      id="nombrePassagers"
                      name="nombrePassagers"
                      value={formData.nombrePassagers}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm pr-8 appearance-none"
                    >
                      {[1,2,3,4,5,6,7,8].map(num => (
                        <option key={num} value={num}>{num} {t('common:passenger')}{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="lieuDepart" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:departureLocation')} {t('common:required')}
                  </label>
                  <input
                    type="text"
                    id="lieuDepart"
                    name="lieuDepart"
                    value={formData.lieuDepart}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderDeparture')}
                  />
                </div>
                <div>
                  <label htmlFor="destination" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:destination')} {t('common:required')}
                  </label>
                  <input
                    type="text"
                    id="destination"
                    name="destination"
                    value={formData.destination}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderDestination')}
                  />
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                  {t('common:message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs sm:text-sm resize-none"
                  placeholder={t('common:messageDescription')}
                ></textarea>
                <div className="text-right text-xs text-charcoal-500 mt-1">
                  {formData.message.length}/500 {t('common:charactersLimit')}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3 sm:py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {t('common:sending')}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    {t('common:sendRequest')}
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-3 sm:p-4 bg-green-100 border border-green-300 rounded-lg text-green-700 text-xs sm:text-sm">
                  <i className="ri-check-line mr-2"></i>
                  {t('common:successMessage')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-3 sm:p-4 bg-red-100 border border-red-300 rounded-lg text-red-700 text-xs sm:text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {t('common:errorMessage')}
                </div>
              )}
            </form>
          </div>

          {/* Informations de contact */}
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
              <h3 className="text-xl sm:text-2xl font-bold text-charcoal-800 mb-4 sm:mb-6">{t('common:directContact')}</h3>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-blue-600 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-charcoal-800 text-sm sm:text-base">{t('common:whatsapp')}</h4>
                    <a href="https://wa.me/212666204299" className="text-blue-600 hover:text-blue-700 cursor-pointer text-sm sm:text-base">
                      +212 666-204299
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-blue-600 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-charcoal-800 text-sm sm:text-base">{t('common:telephone')}</h4>
                    <a href="tel:+212666204299" className="text-blue-600 hover:text-blue-700 cursor-pointer text-sm sm:text-base">
                      +212 666-204299
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-2xl p-4 sm:p-6 md:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-charcoal-800 mb-3 sm:mb-4">{t('common:quickResponse')}</h3>
              <p className="text-charcoal-600 text-sm sm:text-base leading-relaxed">
                {t('common:quickResponseDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReservationSection;
